<template>
  <!-- 主内容区域 -->
  <main class="main-content">
    <!-- 欢迎区域 -->
    <section class="hero">
      <h1>API 安全性指南</h1>
      <p>了解银企通™ API平台的安全机制和最佳实践，确保您的API调用安全可靠</p>
    </section>

    <!-- 安全概述 -->
    <section class="section">
      <div class="section-header">
        <h2>安全概述</h2>
        <p>银企通™ API平台采用多层次安全机制保护您的数据和API调用</p>
      </div>

      <div class="security-container">
        <div class="security-feature">
          <h3><i class="fas fa-key"></i> 认证机制</h3>
          <p>所有API请求都需要通过安全认证，确保只有授权用户才能访问资源。</p>

          <div class="security-level">
            <i class="fas fa-shield-alt"></i>
            <div>
              <strong>安全级别: </strong>
              <span class="security-badge badge-high">高</span>
            </div>
          </div>

          <div class="api-details">
            <pre>
// 请求头示例
Headers:
    x-app-id: [您的AppID]
    x-app-secret: [您的API Key]
  </pre
            >
          </div>
        </div>

        <div class="security-feature">
          <h3><i class="fas fa-lock"></i> 数据加密</h3>
          <p>所有敏感数据在传输和存储过程中都进行加密处理，防止数据泄露。</p>

          <div class="security-level">
            <i class="fas fa-shield-alt"></i>
            <div>
              <strong>安全级别: </strong>
              <span class="security-badge badge-high">高</span>
            </div>
          </div>
        </div>

        <div class="security-feature">
          <h3><i class="fas fa-user-check"></i> 访问控制</h3>
          <p>基于角色的访问控制机制，确保用户只能访问其权限范围内的资源。</p>

          <div class="security-level">
            <i class="fas fa-shield-alt"></i>
            <div>
              <strong>安全级别: </strong>
              <span class="security-badge badge-high">高</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 认证机制 -->
    <section class="section">
      <div class="section-header">
        <h2>认证机制</h2>
        <p>所有API请求都需要通过AppID和API Key进行认证。</p>
      </div>

      <div class="security-container">
        <div class="security-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <h3>获取AppID和API Key</h3>
            <p>在银企通™ API平台的"应用与设置"页面中获取您的AppID和API Key。</p>
            <div class="note">
              <strong>注意：</strong>请妥善保管您的API
              Key，不要将其暴露在客户端代码中。
            </div>
          </div>
        </div>

        <div class="security-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <h3>发起API请求</h3>
            <p>在API请求头中包含x-app-id和x-app-secret参数。</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 国密加密（SM2/SM3/SM4） -->
    <section class="section">
      <div class="section-header">
        <h2>国密加密（SM2/SM3/SM4）</h2>
        <p>
          目前所有接口都支持按照国家密码算法规范，对请求与响应进行加密、摘要与验签。其中支付制单接口建议使用国密加密。
        </p>
      </div>

      <div class="security-container">
        <div class="security-feature">
          <h3><i class="fas fa-certificate"></i> 启用方式</h3>
          <p>
            企业准备一套
            <strong>SM2密钥对</strong
            >（公钥交付给见知，私钥自行安全保存），并同时保存见知提供的
            <strong>见知SM2公钥</strong>用于密钥加密与响应验签。
          </p>
          <div class="note">
            <strong>提示：</strong
            >企业私钥仅用于本地签名与解密，请勿外泄；建议使用硬件加密模块（HSM）或安全密钥管理服务进行保管。
          </div>
        </div>

        <div class="security-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <h3>生成会话密钥（SM4）</h3>
            <p>
              随机生成一个
              <strong>16位字符串</strong>
              作为本次请求的SM4密钥（仅数字和大小写字母，不含特殊字符）。
            </p>
          </div>
        </div>

        <div class="security-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <h3>加密原始报文（SM4）</h3>
            <p>
              将原始报文（将业务请求参数转换为JSON字符串）使用步骤1生成的SM4密钥进行加密，得到密文，赋值给
              <code>reqData</code> 参数。
            </p>
            <div class="api-details">
              <pre>
// 原始报文（示例，无值传空字符串）
{
  "businessTransNo": "",
  "payAmount": "",
  "crossBank": "0",
  "fromAccountNo": "",
  "fromAccountName": "",
  "toAccountNo": "",
  "toAccountName": "",
  "toAccountType": "0",
  "toBankName": "",
  "toBankNo": "",
  "scheduleDate": "",
  "purpose": "",
  "postscript": "",
  "summary": "",
  "metaJson": "{\"projectInfo\":\"测试项目信息\"}"
}
              </pre>
            </div>
          </div>
        </div>

        <div class="security-step">
          <div class="step-number">3</div>
          <div class="step-content">
            <h3>加密会话密钥（SM2）并设置请求头</h3>
            <p>
              使用
              <strong>见知SM2公钥</strong>
              加密步骤1生成的SM4密钥，并将密文放入请求Header：
              <code>xencio-key</code>。
            </p>
            <div class="api-details">
              <pre>
// 请求头示例（叠加认证头）
Headers:
  x-app-id: [您的AppID]
  x-app-secret: [您的API Key]
  xencio-key: [使用见知SM2公钥加密后的SM4密钥]
              </pre>
            </div>
          </div>
        </div>

        <div class="security-step">
          <div class="step-number">4</div>
          <div class="step-content">
            <h3>摘要与签名（SM3 + 企业SM2私钥）</h3>
            <p>
              使用
              <strong>SM3</strong>
              算法对原始报文（未加密的JSON字符串）进行摘要，得到摘要字符串；然后用企业的
              <strong>SM2私钥</strong>
              对摘要进行签名，得到签名字符串，赋值给请求体参数
              <code>sign</code>。
            </p>
          </div>
        </div>

        <div class="security-step">
          <div class="step-number">5</div>
          <div class="step-content">
            <h3>最终请求结构</h3>
            <p>请求体与请求头示例如下：</p>
            <div class="api-details">
              <pre>
// Body
{
  "reqData": "...",   // 使用SM4加密后的密文
  "sign": "..."       // 企业SM2私钥对SM3摘要的签名
}

// Headers
  x-app-id: [您的AppID]
  x-app-secret: [您的API Key]
  xencio-key: [见知SM2公钥加密的SM4密钥]
              </pre>
            </div>
            <div class="note">
              <strong>注意：</strong>请确保 <code>reqData</code> 的密文与
              <code>sign</code> 的摘要源同一份原始报文，以避免验签失败。
            </div>
          </div>
        </div>

        <div class="security-feature">
          <h3><i class="fas fa-reply"></i> 响应解析流程</h3>
          <div class="security-container">
            <div class="security-step">
              <div class="step-number">1</div>
              <div class="step-content">
                <h3>解密会话密钥（SM2私钥）</h3>
                <p>
                  读取响应Header中的 <code>xencio-key</code>，使用企业
                  <strong>SM2私钥</strong> 解密得到SM4密钥。
                </p>
              </div>
            </div>
            <div class="security-step">
              <div class="step-number">2</div>
              <div class="step-content">
                <h3>解密响应报文（SM4）</h3>
                <p>
                  使用步骤1得到的SM4密钥解密响应体中的
                  <code>respData</code>，得到原始响应报文的JSON字符串。
                </p>
              </div>
            </div>
            <div class="security-step">
              <div class="step-number">3</div>
              <div class="step-content">
                <h3>摘要原始响应（SM3）</h3>
                <p>
                  对步骤2得到的原始响应报文执行
                  <strong>SM3</strong> 摘要，得到摘要字符串。
                </p>
              </div>
            </div>
            <div class="security-step">
              <div class="step-number">4</div>
              <div class="step-content">
                <h3>验签（见知SM2公钥）</h3>
                <p>
                  取响应体中的 <code>sign</code> 字符串，与步骤3得到的摘要，使用
                  <strong>见知SM2公钥</strong> 进行验签； 验签一致则通过。
                </p>
                <div class="note">
                  <strong>提示：</strong
                  >原始响应报文即为当前接口的响应参数，请以解密后的JSON字段为准。
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="note">
          <strong>环境提示：</strong>生产与沙盒环境的 <code>AppID</code>、<code
            >API Key</code
          >、以及密钥配置可能不同，请勿混用。
        </div>
      </div>
    </section>

    <!-- 沙盒环境 -->

    <!-- 生产环境 -->
    <section class="section">
      <div class="section-header">
        <h2>注意</h2>
        <p>切换到生产环境前的准备工作</p>
      </div>

      <div class="security-container">
        <div class="security-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <h3>验证配置</h3>
            <p>在生产环境使用前，确保所有API调用都经过充分测试。</p>
          </div>
        </div>

        <div class="security-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <h3>监控和日志</h3>
            <p>设置适当的监控和日志记录机制，以便及时发现和处理安全问题。</p>
          </div>
        </div>
      </div>

      <div class="note">
        <strong>重要：</strong>生产环境和沙盒环境的AppID和API
        Key是不同的，请勿混用。
      </div>
    </section>

    <!-- 最佳实践 -->
    <section class="section">
      <div class="section-header">
        <h2>安全最佳实践</h2>
        <p>遵循这些最佳实践确保API调用的安全性</p>
      </div>

      <div class="security-container">
        <div class="security-feature">
          <h3><i class="fas fa-ban"></i> 保护API Key</h3>
          <p>永远不要将API Key暴露在客户端代码、版本控制系统或公共存储库中。</p>
          <div class="note">
            <strong>建议：</strong>将API Key存储在环境变量或安全的配置管理中。
          </div>
        </div>

        <div class="security-feature">
          <h3><i class="fas fa-sync-alt"></i> 定期轮换密钥</h3>
          <p>定期更换API Key以提高安全性，特别是在员工变动或怀疑密钥泄露时。</p>
        </div>

        <div class="security-feature">
          <h3><i class="fas fa-list"></i> 最小权限原则</h3>
          <p>只为应用分配其功能所需的最小权限，避免过度授权。</p>
        </div>

        <div class="security-feature">
          <h3><i class="fas fa-shield-virus"></i> 使用HTTPS</h3>
          <p>确保所有API调用都通过HTTPS进行，防止数据在传输过程中被窃取。</p>
        </div>
      </div>
    </section>

    <!-- 故障排除 -->
    <section class="section">
      <div class="section-header">
        <h2>常见问题与故障排除</h2>
        <p>解决API调用中可能遇到的安全相关问题</p>
      </div>

      <div class="overflow-x-auto">
      <table class="param-table">
        <thead>
          <tr>
            <th>问题</th>
            <th>可能原因</th>
            <th>解决方案</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>401 Unauthorized</td>
            <td>无效的AppID或API Key</td>
            <td>检查AppID和API Key是否正确生成</td>
          </tr>
          <tr>
            <td>403 Forbidden</td>
            <td>权限不足</td>
            <td>检查应用是否有权限访问该资源</td>
          </tr>
          <tr>
            <td>沙盒到生产切换失败</td>
            <td>使用了错误的环境配置</td>
            <td>确认使用生产环境的AppID和AppSecret</td>
          </tr>
        </tbody>
      </table>
      </div>
    </section>

    <!-- 支持 -->
    <section class="section">
      <div class="section-header">
        <h2>获取支持</h2>
        <p>需要帮助？通过以下方式联系我们</p>
      </div>

      <div class="card-grid">
        <div class="card">
          <div class="card-icon">
            <i class="fas fa-book"></i>
          </div>
          <h3>查看文档</h3>
          <p>阅读详细的API文档和示例代码。</p>
          <a href="introduction" class="card-link"
            >查看文档 <i class="fas fa-arrow-right"></i
          ></a>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fas fa-question-circle"></i>
          </div>
          <h3>常见问题</h3>
          <p>查找常见问题的解答。</p>
          <a href="faq" class="card-link"
            >查看FAQ <i class="fas fa-arrow-right"></i
          ></a>
        </div>

        <!-- <div class="card">
          <div class="card-icon">
            <i class="fas fa-headset"></i>
          </div>
          <h3>联系客服</h3>
          <p>获取技术支持和生产环境配置。</p>
          <a href="#" class="card-link"
            >联系客服 <i class="fas fa-arrow-right"></i
          ></a>
        </div> -->
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { useCodeHighlight } from "@/composables/useCodeHighlight";

// 使用代码高亮功能
useCodeHighlight("pre code");
</script>

<style scoped>
.hero {
  background: linear-gradient(120deg, #7486f1, #955bea);
  border-radius: 12px;
  padding: 2.5rem;
  color: white;
  margin-bottom: 2rem;
}

.hero h1 {
  font-size: 2.25rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.hero p {
  font-size: 1.1rem;
  max-width: 700px;
  margin-bottom: 1.5rem;
  opacity: 0.9;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.card {
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background-color: rgba(58, 109, 248, 0.1);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.card h3 {
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
}

.card p {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.card-link {
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.card-link i {
  margin-left: 0.5rem;
  transition: transform 0.2s;
}

.card-link:hover i {
  transform: translateX(3px);
}

.section {
  margin-bottom: 3rem;
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-header h2 {
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.section-header p {
  color: var(--text-secondary);
}

.api-reference {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.api-item {
  border-bottom: 1px solid var(--border);
  padding: 1.5rem;
}

.api-item:last-child {
  border-bottom: none;
}

.api-item-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.api-method {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 0.75rem;
}

.method-get {
  background-color: rgba(71, 184, 129, 0.1);
  color: #47b881;
}

.method-post {
  background-color: rgba(58, 109, 248, 0.1);
  color: var(--primary);
}

.api-url {
  font-size: 0.9rem;
  color: var(--text-primary);
}

.api-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.api-details {
  background-color: var(--code-bg);
  color: var(--code-color);
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.85rem;
  overflow-x: auto;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.api-details pre {
  margin: 0;
  white-space: pre-wrap;
}

.try-section {
  background-color: var(--secondary);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.try-section h4 {
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.9rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.9rem;
}

.response-area {
  margin-top: 1.5rem;
  background-color: var(--code-bg);
  border-radius: 8px;
  padding: 1rem;
  color: var(--code-color);
  font-size: 0.85rem;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}

.response-area pre {
  margin: 0;
  white-space: pre-wrap;
}

.param-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.param-table th,
.param-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

.param-table th {
  background-color: var(--secondary);
  font-weight: 600;
}

/* 安全性特定样式 */
.security-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 2rem 0;
}

.security-step {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.step-number {
  background: var(--primary);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-content h3 {
  color: var(--text-primary);
}

.step-content p {
  color: var(--text-secondary);
  margin-bottom: 1rem;
}

.image-placeholder {
  background: var(--secondary);
  border: 2px dashed var(--border);
  border-radius: 8px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
  color: var(--text-secondary);
}

.image-placeholder i {
  font-size: 3rem;
  margin-right: 1rem;
}

.note {
  background: rgba(255, 193, 7, 0.1);
  border-left: 4px solid #ffc107;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
}

.status-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.status-table th,
.status-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

.status-table th {
  background-color: var(--secondary);
  font-weight: 600;
}

.status-table tr:last-child td {
  border-bottom: none;
}

/* 安全性特定样式 */
.security-feature {
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
}

.security-feature h3 {
  color: var(--primary);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

@media (max-width: 767px) {
  .security-feature h3 {
    margin-bottom: 0;
  }
}

.security-feature h3 i {
  margin-right: 0.75rem;
}

.security-level {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: var(--secondary);
  border-radius: 6px;
}

.security-level i {
  margin-right: 0.75rem;
  color: var(--accent);
}

.security-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.75rem;
}

.badge-high {
  background-color: rgba(235, 87, 87, 0.1);
  color: #eb5757;
}

.badge-medium {
  background-color: rgba(242, 201, 76, 0.1);
  color: #f2c94c;
}

.badge-low {
  background-color: rgba(71, 184, 129, 0.1);
  color: #47b881;
}
</style>
